import { createContext, useState, useEffect, useCallback } from 'react';

export const AuthContext = createContext(null);

// Hint that a session may exist, so anonymous visitors on the public
// site never hit /api/auth/me (avoids a guaranteed 401 on every visit).
const AUTH_HINT_KEY = 'briljans_auth';

const hasAuthHint = () => {
  try {
    return localStorage.getItem(AUTH_HINT_KEY) === '1';
  } catch {
    return false;
  }
};

const setAuthHint = (value) => {
  try {
    if (value) localStorage.setItem(AUTH_HINT_KEY, '1');
    else localStorage.removeItem(AUTH_HINT_KEY);
  } catch {
    // Ignore (storage unavailable)
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        setAuthHint(true);
      } else {
        setUser(null);
        setAuthHint(false);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const path = window.location.pathname;
    const onPortalPage = path.startsWith('/portal') || path.startsWith('/admin');
    if (onPortalPage || hasAuthHint()) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [fetchUser]);

  const login = async (email, password) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Inloggningen misslyckades');
    }

    setUser(data.user);
    setAuthHint(true);
    return data.user;
  };

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'include',
      });
    } catch {
      // Ignore errors
    }
    setUser(null);
    setAuthHint(false);
  };

  const refreshToken = async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        setAuthHint(true);
        return true;
      }
    } catch {
      // Ignore
    }
    setUser(null);
    setAuthHint(false);
    return false;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
}
